import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Briefcase, TrendingUp, DollarSign, Check } from 'lucide-react';

const BoardTypeSelector = ({ open, onClose, onSelect }) => {
  const [selectedType, setSelectedType] = useState('project');

  const boardTypes = [
    {
      id: 'project',
      icon: Briefcase,
      title: 'Project Management',
      description: 'Plan & track deliverables',
      color: 'from-blue-50 to-blue-100',
      iconColor: 'text-blue-600',
      borderColor: 'border-blue-300',
      features: ['Task tracking', 'Progress monitoring', 'Team collaboration']
    },
    {
      id: 'marketing',
      icon: TrendingUp,
      title: 'Marketing',
      description: 'Manage campaigns & content',
      color: 'from-orange-50 to-orange-100',
      iconColor: 'text-orange-600',
      borderColor: 'border-orange-300',
      features: ['Campaign planning', 'Content calendar', 'Performance tracking']
    },
    {
      id: 'sales',
      icon: DollarSign,
      title: 'Sales',
      description: 'Track leads & deals',
      color: 'from-green-50 to-green-100',
      iconColor: 'text-green-600',
      borderColor: 'border-green-300',
      features: ['Pipeline management', 'Deal tracking', 'Client relationships']
    }
  ];

  const handleSelect = () => {
    onSelect(selectedType);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-2xl">Choose Board Type</DialogTitle>
          <DialogDescription>
            Select the purpose of this board to automatically configure its structure
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          {boardTypes.map((type) => {
            const Icon = type.icon;
            const isSelected = selectedType === type.id;

            return (
              <div
                key={type.id}
                onClick={() => setSelectedType(type.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setSelectedType(type.id);
                  }
                }}
                className={`
                  relative cursor-pointer rounded-xl border-2 transition-all pointer-events-auto
                  ${isSelected 
                    ? `${type.borderColor} shadow-lg scale-105` 
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                  }
                `}
              >
                {/* Selected checkmark */}
                {isSelected && (
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-[#1C3E53] rounded-full flex items-center justify-center shadow-lg">
                    <Check className="h-4 w-4 text-white" />
                  </div>
                )}

                {/* Card content */}
                <div className={`p-6 bg-gradient-to-br ${type.color} rounded-xl`}>
                  {/* Icon */}
                  <div className={`w-12 h-12 rounded-lg bg-white shadow-sm flex items-center justify-center mb-4`}>
                    <Icon className={`h-6 w-6 ${type.iconColor}`} />
                  </div>

                  {/* Title & Description */}
                  <h3 className="font-bold text-lg text-gray-900 mb-1">
                    {type.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    {type.description}
                  </p>

                  {/* Features */}
                  <ul className="space-y-1">
                    {type.features.map((feature, index) => (
                      <li key={index} className="text-xs text-gray-700 flex items-center gap-1">
                        <span className={`w-1 h-1 rounded-full ${type.iconColor.replace('text', 'bg')}`}></span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center mt-6 pt-4 border-t">
          <p className="text-sm text-gray-500">
            You can customize columns and fields after creation
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleSelect}
              className="bg-[#1C3E53] hover:bg-[#E5602F]"
            >
              Continue
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BoardTypeSelector;
